import PropTypes from 'prop-types';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { shouldTranslateByDefault } from '@icp/settings';
import { HELPER_TEXT_TYPES, helpersIsEmpty } from '@icp/form-renderer-core';
import { useElementDecorator, useIsInDesign } from '../../FormRenderCtx';
import { useClassName, useDataSourceSupportLazy, useHttpBody } from '../../hooks';
import { withFieldWrapper } from '../../fieldWrapper';
import { ConditionalPropertyPropType, SingleDataSourceTypes } from '../../propTypes';
import { DEFAULT_MAPPING, formatOptions } from '../SelectElement/utils';
import AutoCompleteUI from './AutoCompleteUI';

const AutoCompleteElement = forwardRef(function AutoCompleteElement(props, ref) {
  const {
    keyPath,
    id,
    className: classNameProp,
    value: valueProp,
    style,
    componentProps = {},
    readonly,
    helpers,
    onChange,
  } = props;

  const {
    mapping = DEFAULT_MAPPING,
    options: optionsProp,
    dataSource,
    dataUrl,
    dataUrlResolveMode,
    dataFilters,
    sortModel,
    dataResponseKeyPath = 'results',
    transformDataResponse,
    translateDataResponse = shouldTranslateByDefault(),
    debounceTime,
    defaultValue,
    httpMethod,
    httpBody: httpBodyProp,
    selectColId,
    needCount = false,
    searchTextColIds = [mapping.label],
    requireSearchToFetch = false,
    alwaysFetchOnSearch = false,
    itemField,
    onSearch,
    onSelectOption,
    ...otherComponentProps
  } = componentProps;

  const ElementDecorator = useElementDecorator();
  const isInDesign = useIsInDesign();

  const className = useClassName(classNameProp);
  const classNameComp = useClassName(componentProps.className);

  const [searchText, setSearchText] = useState('');

  const nodeRef = useRef(null);

  const httpBody = useHttpBody(httpBodyProp);

  // 当 requireSearchToFetch 为 true 时，没有搜索条件则不发请求
  const skipFetch = isInDesign || !!optionsProp || (requireSearchToFetch && !searchText);

  const { loading, dataFetched, allDataIsFetched } = useDataSourceSupportLazy({
    skip: skipFetch,
    lazy: false,
    searchText,
    searchTextColIds,
    debounceLeading: !searchText,
    cacheBlockSize: 30,
    // single data source config
    dataSource,
    dataUrl,
    dataResponseKeyPath,
    dataFilters,
    sortModel,
    transformDataResponse,
    translateDataResponse,
    debounceTime,
    defaultValue,
    httpMethod,
    httpBody,
    selectColId,
    needCount,
    dataUrlResolveMode,
  });

  const options = useMemo(() => {
    return formatOptions({
      options: optionsProp || dataFetched,
      mapping,
      stringEqual: false,
      dataExclusion: undefined,
    });
  }, [mapping, dataFetched, optionsProp]);

  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
      getOptions: () => options,
    }),
    [options],
  );

  const useClientSideFilter = !alwaysFetchOnSearch && (allDataIsFetched || !!optionsProp);

  const readonlyText = useMemo(() => {
    if (!readonly) return '';
    return valueProp ?? '';
  }, [readonly, valueProp]);

  const handleChange = (newValue) => {
    // AutoComplete 存储的是简单的 string value
    onChange(newValue ?? '');

    // 当用户选中了一个 option 时，通知调用方完整的 option 数据
    if (onSelectOption && newValue) {
      const selectedOption = options.find(
        (op) => String(op[mapping.value] ?? '') === String(newValue),
      );
      if (selectedOption) {
        onSelectOption(selectedOption, newValue);
      }
    }
  };

  const handleSearch = (newSearchText) => {
    if (!useClientSideFilter) {
      setSearchText(newSearchText);
    }
    if (onSearch) {
      onSearch(newSearchText);
    }
  };

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <div
        className={clsx(
          'auto-complete-element',
          'input-element',
          'form-element',
          {
            'has-helper': !helpersIsEmpty(helpers),
          },
          className,
        )}
        style={style}
        ref={nodeRef}
      >
        <AutoCompleteUI
          {...props}
          componentProps={{
            ...otherComponentProps,
            className: classNameComp,
          }}
          readonlyText={readonlyText}
          options={loading ? [] : options}
          value={valueProp ?? ''}
          mapping={mapping}
          searchTextColIds={searchTextColIds}
          loading={loading}
          itemField={itemField}
          onChange={handleChange}
          onSearch={handleSearch}
        />
      </div>
    </ElementDecorator>
  );
});

AutoCompleteElement.propTypes = {
  fieldApi: PropTypes.func,
  keyPath: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.string,
  componentProps: PropTypes.shape({
    className: PropTypes.string,
    style: PropTypes.shape({}),
    mapping: PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
      }),
    ),
    ...SingleDataSourceTypes,
    searchTextColIds: PropTypes.arrayOf(PropTypes.string),
    /**
     * 是否需要有搜索条件才发请求，没有搜索内容时不加载数据
     * @default false
     */
    requireSearchToFetch: PropTypes.bool,
    /**
     * 搜索条件变化时是否始终重新发请求，不使用客户端过滤
     * @default false
     */
    alwaysFetchOnSearch: PropTypes.bool,
    /**
     * 当用户从下拉列表选中一个 option 时触发，传入完整的 option 对象和选中的 value
     * @param {Object} option - 选中的完整 option 数据
     * @param {string} value - 选中的 value 值
     */
    onSelectOption: PropTypes.func,
    itemField: PropTypes.shape({}),
  }),
  fieldTitleProps: PropTypes.shape({
    showColon: PropTypes.bool,
  }),
  validation: PropTypes.shape({
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    maxLengthErrorMessage: PropTypes.string,
  }),
  disabled: ConditionalPropertyPropType(PropTypes.bool),
  readonly: ConditionalPropertyPropType(PropTypes.bool),
  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onTouchChanged: PropTypes.func,
};

// for @icp/utils/getComponentDisplayName
AutoCompleteElement.displayName = 'AutoComplete';

export default withFieldWrapper(AutoCompleteElement);
